import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import ImageIdFrontFail from 'images/pictographs/id-front-fail.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import { IMAGE_LOCAL_DIMENSION_DEFAULTS } from 'inquiry/components/ImageLocal/ImageLocal';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import {
  AutoclassificationFailureReason,
  AutoclassificationResponseType,
} from 'inquiry/redux/thunk-actions/verification';
import { colorCss, sizeCss } from 'lib/inquiryStyles';
import { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { ImageKey } from 'lib/interfaces/ui-components';
import ThemedSvg from 'verify/components/ThemedSvg';

// Note: added for start-aligned FailureReason ThemedSvg
const FailureReasonPictograph = styled.div<{ justify?: string }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ justify }) => justify ?? 'center'};
`;

interface InquiryGovernmentIdAutoclassificationFailureViewProps {
  localizations: StepGovernmentIdConfig['localizations']['auto-classification-page'] | undefined;
  autoclassificationFailureReason: AutoclassificationFailureReason | undefined;
  handleContinue: () => void;
}

export default function InquiryGovernmentIdAutoclassificationFailureView({
  localizations,
  autoclassificationFailureReason,
  handleContinue,
}: InquiryGovernmentIdAutoclassificationFailureViewProps) {
  const {
    base,
    buttons: buttonStyles,
    imageLocal: imageLocalStyles,
  } = useStepGovernmentIdStyleContext();

  const isIdRejected = autoclassificationFailureReason === AutoclassificationResponseType.REJECTED;

  const height = sizeCss(imageLocalStyles?.illustration?.height?.base.base);
  const width = sizeCss(imageLocalStyles?.illustration?.width?.base.base);
  const marginTop = sizeCss(imageLocalStyles?.illustration?.margin?.base.base?.top);
  const marginBottom = sizeCss(imageLocalStyles?.illustration?.margin?.base.base?.bottom);
  const marginLeft = sizeCss(imageLocalStyles?.illustration?.margin?.base.base?.left);
  const marginRight = sizeCss(imageLocalStyles?.illustration?.margin?.base.base?.right);
  const justify = imageLocalStyles?.illustration?.justify.base.base;
  const colorPrimary = colorCss(imageLocalStyles?.illustration?.['fill-color']?.base?.base);
  const colorBackground = colorCss(imageLocalStyles?.illustration?.['fill-color']?.background.base);
  const colorHighlight = colorCss(imageLocalStyles?.illustration?.['fill-color']?.highlight.base);
  const colorStroke = colorCss(imageLocalStyles?.illustration?.['stroke-color']?.base?.base);

  const colorProps = {
    $primaryColor: colorPrimary,
    $backgroundColor: colorBackground,
    $highlightColor: colorHighlight,
    $strokeColor: colorStroke,
  };

  const fallbackDimensions = IMAGE_LOCAL_DIMENSION_DEFAULTS[ImageKey.IdFrontIcon];

  const title =
    (isIdRejected
      ? localizations?.['id-class-rejected-title']
      : localizations?.['unable-to-classify-document-title']) ?? 'Failed to autoclassify';
  const continueButtonText =
    (isIdRejected
      ? localizations?.['id-class-rejected-continue-button-text']
      : localizations?.['unable-to-classify-document-continue-button-text']) ?? 'Continue';

  return (
    <>
      <ThemedTitle id='autoclassification-failure__title' styles={base?.titleStyle}>
        <Markdown>{title}</Markdown>
      </ThemedTitle>

      <FailureReasonPictograph role='img' aria-label='Failed icon' justify={justify}>
        <ThemedSvg {...colorProps}>
          <ImageIdFrontFail
            style={{
              width: width ?? sizeCss(fallbackDimensions.width),
              height: height ?? sizeCss(fallbackDimensions.height),
              marginTop,
              marginBottom,
              marginLeft,
              marginRight,
            }}
          />
        </ThemedSvg>
      </FailureReasonPictograph>
      <Spacer height={6} />
      <Button
        type='button'
        id='autoclassification-failure__btn'
        buttonType={ButtonType.Primary}
        styles={buttonStyles?.[ButtonType.Primary]}
        onClick={handleContinue}
      >
        {continueButtonText}
      </Button>
    </>
  );
}
